export const LOCALE_COOKIE_NAME = "NEXT_LOCALE"
export const REQUEST_LOCALE_HEADER = "x-pleasur-locale"
export const REQUEST_BLOG_LOCALE_HEADER = "x-pleasur-blog-locale"

export const supportedLocales = [
  "en",
  "de",
  "nl",
  "es",
  "fr",
  "id",
  "it",
  "pl",
  "pt",
  "sv",
  "tr",
  "uk",
  "ru",
  "zh",
  "ja",
  "ko",
] as const

export type UiLocale = (typeof supportedLocales)[number]

export interface LocaleMeta {
  code: UiLocale
  label: string
  nativeLabel: string
  flagLabel: string
}

export const defaultLocale = "en" as const satisfies UiLocale

export const interfaceOnlyLocales = [
  "id",
  "it",
  "pl",
  "pt",
  "sv",
  "tr",
  "uk",
  "zh",
  "ko",
] as const satisfies readonly UiLocale[]

export const phaseOneLocales = [
  "en",
  "fr",
  "de",
  "es",
  "ja",
  "nl",
  ...interfaceOnlyLocales,
] as const satisfies readonly UiLocale[]

// Blog translations are independent from chat and character localization.
// Every UI locale can have its own CMS article translation even when that
// locale remains interface-only elsewhere in the product.
export const supportedBlogLocales = supportedLocales
export type BlogLocale = (typeof supportedBlogLocales)[number]

export const localeMeta: Record<UiLocale, LocaleMeta> = {
  en: { code: "en", label: "English", nativeLabel: "English", flagLabel: "English" },
  de: { code: "de", label: "German", nativeLabel: "Deutsch", flagLabel: "German" },
  nl: { code: "nl", label: "Dutch", nativeLabel: "Nederlands", flagLabel: "Dutch" },
  es: { code: "es", label: "Spanish", nativeLabel: "Español", flagLabel: "Spanish" },
  fr: { code: "fr", label: "French", nativeLabel: "Français", flagLabel: "French" },
  id: { code: "id", label: "Indonesian", nativeLabel: "Bahasa Indonesia", flagLabel: "Indonesian" },
  it: { code: "it", label: "Italian", nativeLabel: "Italiano", flagLabel: "Italian" },
  pl: { code: "pl", label: "Polish", nativeLabel: "Polski", flagLabel: "Polish" },
  pt: { code: "pt", label: "Portuguese", nativeLabel: "Português", flagLabel: "Portuguese" },
  sv: { code: "sv", label: "Swedish", nativeLabel: "Svenska", flagLabel: "Swedish" },
  tr: { code: "tr", label: "Turkish", nativeLabel: "Türkçe", flagLabel: "Turkish" },
  uk: { code: "uk", label: "Ukrainian", nativeLabel: "Українська", flagLabel: "Ukrainian" },
  ru: { code: "ru", label: "Russian", nativeLabel: "Русский", flagLabel: "Russian" },
  zh: { code: "zh", label: "Chinese", nativeLabel: "中文", flagLabel: "Chinese" },
  ja: { code: "ja", label: "Japanese", nativeLabel: "日本語", flagLabel: "Japanese" },
  ko: { code: "ko", label: "Korean", nativeLabel: "한국어", flagLabel: "Korean" },
}

const intlLocaleByUiLocale: Record<UiLocale, string> = {
  en: "en-US",
  de: "de-DE",
  nl: "nl-NL",
  es: "es-ES",
  fr: "fr-FR",
  id: "id-ID",
  it: "it-IT",
  pl: "pl-PL",
  pt: "pt-PT",
  sv: "sv-SE",
  tr: "tr-TR",
  uk: "uk-UA",
  ru: "ru-RU",
  zh: "zh-CN",
  ja: "ja-JP",
  ko: "ko-KR",
}

const supportedLocaleSet = new Set<UiLocale>(supportedLocales)
const interfaceOnlyLocaleSet = new Set<UiLocale>(interfaceOnlyLocales)
const supportedBlogLocaleSet = new Set<UiLocale>(supportedBlogLocales)

function parseLocaleList(raw: string | undefined): UiLocale[] {
  if (!raw?.trim()) return []
  const locales: UiLocale[] = []
  for (const item of raw.split(",")) {
    const locale = item.trim().toLowerCase()
    if (isSupportedLocale(locale) && !locales.includes(locale)) {
      locales.push(locale)
    }
  }
  return locales
}

export function isSupportedLocale(value: string | null | undefined): value is UiLocale {
  return supportedLocaleSet.has(value as UiLocale)
}

export function getIntlLocale(locale: string | null | undefined): string {
  return isSupportedLocale(locale)
    ? intlLocaleByUiLocale[locale]
    : intlLocaleByUiLocale[defaultLocale]
}

export function isInterfaceOnlyLocale(value: string | null | undefined): value is UiLocale {
  return isSupportedLocale(value) && interfaceOnlyLocaleSet.has(value)
}

export function getEnabledLocales(): UiLocale[] {
  const configured = parseLocaleList(process.env.NEXT_PUBLIC_ENABLED_LOCALES)
  if (configured.length > 0) {
    return configured.includes(defaultLocale) ? configured : [defaultLocale, ...configured]
  }
  return [...phaseOneLocales]
}

export function isEnabledLocale(value: string | null | undefined): value is UiLocale {
  return isSupportedLocale(value) && getEnabledLocales().includes(value)
}

export function getEnabledBlogLocales(): BlogLocale[] {
  const configured = parseLocaleList(
    process.env.NEXT_PUBLIC_ENABLED_BLOG_LOCALES ?? process.env.ENABLED_BLOG_LOCALES
  ).filter(
    (locale): locale is BlogLocale => isSupportedBlogLocale(locale)
  )
  if (configured.length > 0) {
    return configured.includes(defaultLocale) ? configured : [defaultLocale, ...configured]
  }
  return [defaultLocale]
}

export function isSupportedBlogLocale(value: string | null | undefined): value is BlogLocale {
  return isSupportedLocale(value) && supportedBlogLocaleSet.has(value)
}

export function isEnabledBlogLocale(value: string | null | undefined): value is BlogLocale {
  return isSupportedBlogLocale(value) && getEnabledBlogLocales().includes(value)
}

export function getBlogContentLocaleForUiLocale(locale: UiLocale): BlogLocale {
  return isSupportedBlogLocale(locale) ? locale : defaultLocale
}

export function getCharacterContentLocaleForUiLocale(locale: UiLocale): UiLocale {
  return isInterfaceOnlyLocale(locale) ? defaultLocale : locale
}

function splitPath(pathname: string): string[] {
  return pathname.split("/").filter(Boolean)
}

export function getPathLocale(pathname: string): UiLocale | null {
  const firstSegment = splitPath(pathname)[0]
  return isSupportedLocale(firstSegment) ? firstSegment : null
}

export function isLocalizedPath(pathname: string): boolean {
  return getPathLocale(pathname) !== null
}

export function stripLocaleFromPath(pathname: string): string {
  const segments = splitPath(pathname)
  if (segments.length === 0) return "/"
  if (!isSupportedLocale(segments[0])) return pathname || "/"
  const stripped = `/${segments.slice(1).join("/")}`
  return stripped === "/" ? "/" : stripped.replace(/\/$/, "")
}

export function localizePath(pathname: string, locale: UiLocale): string {
  const match = pathname.match(/^([^?#]*)(.*)$/)
  const pathOnly = match?.[1] || "/"
  const suffix = match?.[2] ?? ""
  const stripped = stripLocaleFromPath(pathOnly)
  if (locale === defaultLocale) return `${stripped}${suffix}`
  if (stripped === "/") return `/${locale}${suffix}`
  return `/${locale}${stripped}${suffix}`
}

export function encodePathSegment(segment: string): string {
  return Array.from(segment, (char) => {
    if (char === "%") return "%25"
    if (char === "/") return "%2F"
    if (char === "?") return "%3F"
    if (char === "#") return "%23"

    const codePoint = char.codePointAt(0) ?? 0
    return codePoint <= 31 || codePoint === 127 || /\s/.test(char)
      ? encodeURIComponent(char)
      : char
  }).join("")
}

export function blogIndexPath(locale: UiLocale = defaultLocale): string {
  return localizePath("/blog", locale)
}

export function blogArchivePath(locale: UiLocale = defaultLocale): string {
  return localizePath("/blog/archive", locale)
}

export function blogPostPath(slug: string, locale: UiLocale = defaultLocale): string {
  return localizePath(`/blog/${encodePathSegment(slug)}`, locale)
}

export function normalizeLocale(value: string | null | undefined): UiLocale | null {
  if (!value) return null
  const normalized = value.toLowerCase().split("-")[0]
  return isSupportedLocale(normalized) ? normalized : null
}

export function resolveBrowserLocale(acceptLanguage: string | null | undefined): UiLocale {
  if (!acceptLanguage) return defaultLocale

  const weighted = acceptLanguage
    .split(",")
    .map((part) => {
      const [tag, rawQuality] = part.trim().split(";q=")
      const quality = rawQuality ? Number.parseFloat(rawQuality) : 1
      return { locale: normalizeLocale(tag), quality: Number.isFinite(quality) ? quality : 0 }
    })
    .filter((entry): entry is { locale: UiLocale; quality: number } => entry.locale !== null)
    .sort((a, b) => b.quality - a.quality)

  const enabled = getEnabledLocales()
  return weighted.find((entry) => enabled.includes(entry.locale))?.locale ?? defaultLocale
}
